import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, FolderOpen, FileText, Link2, Plus } from 'lucide-react';
import { useFolders } from '@/api/folders';
import { useKnowledgeItems } from '@/api/knowledge';
import { useDrawingSets, useRevisions } from '@/api/drawing-sets';
import { useMobileRole } from '@/hooks/useMobileRole';
import MobileFileActionSheet from './MobileFileActionSheet';
import MobileUploadSheet from '../knowledge/MobileUploadSheet';
import MoveToFolderModal from './MoveToFolderModal';
import type { KnowledgeItem } from '@estimator/shared';

type StackEntry =
  | { type: 'root' }
  | { type: 'folder'; id: string; name: string; folderType?: string }
  | { type: 'drawing-set'; id: string; name: string; folderId: string };

interface MobileKnowledgeBrowserProps {
  projectId: string;
  onOpenViewer?: (knowledgeItemId: string, revisionId?: string) => void;
}

export default function MobileKnowledgeBrowser({
  projectId,
  onOpenViewer,
}: MobileKnowledgeBrowserProps) {
  const [stack, setStack] = useState<StackEntry[]>([{ type: 'root' }]);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [moveItem, setMoveItem] = useState<KnowledgeItem | null>(null);

  const { canUpload } = useMobileRole();

  const current = stack[stack.length - 1];

  const push = useCallback((entry: StackEntry) => {
    setStack((s) => [...s, entry]);
  }, []);

  const pop = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  // Get the current folder ID for uploads
  const currentFolderId =
    current.type === 'folder' ? current.id :
    current.type === 'drawing-set' ? current.folderId :
    undefined;

  const headerTitle =
    current.type === 'root' ? 'Knowledge' :
    current.type === 'folder' ? current.name :
    current.name;

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {current.type !== 'root' && (
          <button
            onClick={pop}
            className="p-1 -ml-1 rounded-md hover:bg-[var(--bg-hover)]"
          >
            <ChevronLeft size={20} style={{ color: 'var(--accent)' }} />
          </button>
        )}
        <span
          className="text-sm font-semibold truncate"
          style={{ color: 'var(--text-primary)' }}
        >
          {headerTitle}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {current.type === 'root' && (
          <RootFolderList projectId={projectId} onSelect={push} />
        )}
        {current.type === 'folder' && (
          <FolderContentList
            projectId={projectId}
            folderId={current.id}
            folderType={current.folderType}
            onSelectFolder={push}
            onSelectFile={(file) => { setSelectedFile(file); setShowActionSheet(true); }}
            onSelectSet={(set) => push({ type: 'drawing-set', id: set.id, name: set.name, folderId: current.id })}
          />
        )}
        {current.type === 'drawing-set' && (
          <DrawingSetContentList
            projectId={projectId}
            setId={current.id}
            onSelectFile={(file) => { setSelectedFile(file); setShowActionSheet(true); }}
          />
        )}
      </div>

      {/* FAB */}
      {canUpload && (
        <button
          onClick={() => setShowUpload(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center z-50 shadow-lg"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          <Plus size={24} className="text-white" />
        </button>
      )}

      {/* Action sheet */}
      <MobileFileActionSheet
        isOpen={showActionSheet}
        onClose={() => { setShowActionSheet(false); setSelectedFile(null); }}
        file={selectedFile}
        projectId={projectId}
        onMoveToFolder={() => { setMoveItem(selectedFile); setShowActionSheet(false); }}
        onOpenViewer={onOpenViewer ? (id) => onOpenViewer(id) : undefined}
      />

      {/* Upload sheet */}
      <MobileUploadSheet
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        projectId={projectId}
        folderId={currentFolderId}
      />

      {/* Move to folder modal */}
      {moveItem && (
        <MoveToFolderModal
          isOpen={!!moveItem}
          onClose={() => setMoveItem(null)}
          projectId={projectId}
          item={moveItem}
        />
      )}
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────── */

function RootFolderList({
  projectId,
  onSelect,
}: {
  projectId: string;
  onSelect: (entry: StackEntry) => void;
}) {
  const { data: foldersResponse, isLoading } = useFolders(projectId, 'root');
  const folders = foldersResponse?.data ?? [];

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div>
      {folders.map((folder) => (
        <ListItem
          key={folder.id}
          icon={<FolderOpen size={20} style={{ color: 'var(--accent)' }} />}
          title={folder.name}
          subtitle={`${folder.children_count ?? 0} items`}
          drillable
          onClick={() =>
            onSelect({
              type: 'folder',
              id: folder.id,
              name: folder.name,
              folderType: folder.folder_type,
            })
          }
        />
      ))}
    </div>
  );
}

function FolderContentList({
  projectId,
  folderId,
  folderType,
  onSelectFolder,
  onSelectFile,
  onSelectSet,
}: {
  projectId: string;
  folderId: string;
  folderType?: string;
  onSelectFolder: (entry: StackEntry) => void;
  onSelectFile: (file: any) => void;
  onSelectSet: (set: any) => void;
}) {
  // Subfolders
  const { data: subfoldersResponse } = useFolders(projectId, folderId);
  const subfolders = subfoldersResponse?.data ?? [];

  // Knowledge items (client-side filter by folder_id, same pattern as ContentColumn.tsx)
  const { data: itemsResponse } = useKnowledgeItems(projectId);
  const folderItems = (itemsResponse?.data ?? []).filter(
    (item) => item.folder_id === folderId,
  );

  // Drawing sets (only for SHOP_DRAWINGS folders)
  const isShopDrawings = folderType === 'SHOP_DRAWINGS';
  const { data: setsResponse } = useDrawingSets(projectId, isShopDrawings ? folderId : null);
  const drawingSets = setsResponse?.data ?? [];

  return (
    <div>
      {/* Subfolders */}
      {subfolders.map((folder) => (
        <ListItem
          key={folder.id}
          icon={<FolderOpen size={20} style={{ color: 'var(--accent)' }} />}
          title={folder.name}
          subtitle={`${folder.children_count ?? 0} items`}
          drillable
          onClick={() =>
            onSelectFolder({
              type: 'folder',
              id: folder.id,
              name: folder.name,
              folderType: folder.folder_type,
            })
          }
        />
      ))}

      {/* Drawing sets (shop drawings only) */}
      {isShopDrawings && drawingSets.map((set) => (
        <ListItem
          key={set.id}
          icon={<FileText size={20} style={{ color: '#f59e0b' }} />}
          title={set.name}
          subtitle={`Drawing Set · ${set.status}`}
          drillable
          onClick={() => onSelectSet(set)}
        />
      ))}

      {/* Files */}
      {folderItems.map((item) => (
        <ListItem
          key={item.id}
          icon={<FileIcon mimeType={item.mime_type} title={item.title} />}
          title={item.title}
          subtitle={formatFileSubtitle(item)}
          onClick={() => onSelectFile(item)}
        />
      ))}

      {subfolders.length === 0 && folderItems.length === 0 && drawingSets.length === 0 && (
        <EmptyState message="No items in this folder" />
      )}
    </div>
  );
}

function DrawingSetContentList({
  projectId,
  setId,
  onSelectFile,
}: {
  projectId: string;
  setId: string;
  onSelectFile: (file: any) => void;
}) {
  // Use existing useRevisions hook from drawing-sets API
  const { data: revisionsResponse } = useRevisions(setId);
  const revisions = revisionsResponse?.data ?? [];

  return (
    <div>
      {(Array.isArray(revisions) ? revisions : []).map((rev: any) => (
        <ListItem
          key={rev.id}
          icon={<FileText size={20} style={{ color: '#dc3545' }} />}
          title={`Revision ${rev.revision_number}`}
          subtitle={`${rev.status} · ${new Date(rev.created_at).toLocaleDateString()}`}
          onClick={() => onSelectFile({
            id: rev.knowledge_item_id,
            title: `Revision ${rev.revision_number}`,
            mime_type: 'application/pdf',
            file_size: null,
            created_at: rev.created_at,
          })}
        />
      ))}
      {(!Array.isArray(revisions) || revisions.length === 0) && (
        <EmptyState message="No revisions yet" />
      )}
    </div>
  );
}

/* ── Shared UI pieces ────────────────────────────────────────── */

function ListItem({
  icon,
  title,
  subtitle,
  drillable,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  drillable?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-hover)] transition-colors"
      style={{ borderBottom: '1px solid var(--bg-panel)' }}
      onClick={onClick}
    >
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
          {title}
        </div>
        {subtitle && (
          <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {subtitle}
          </div>
        )}
      </div>
      {drillable && <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
    </button>
  );
}

function FileIcon({ mimeType, title }: { mimeType: string | null; title: string }) {
  if (!mimeType) return <Link2 size={20} style={{ color: '#6366f1' }} />;
  if (mimeType === 'application/pdf') {
    return (
      <div className="w-8 h-8 rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: '#dc3545' }}>
        PDF
      </div>
    );
  }
  if (mimeType.startsWith('image/')) {
    const ext = title.split('.').pop()?.toUpperCase() ?? 'IMG';
    return (
      <div className="w-8 h-8 rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: '#28a745' }}>
        {ext}
      </div>
    );
  }
  return <FileText size={20} style={{ color: 'var(--text-muted)' }} />;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</span>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-12">
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{message}</span>
    </div>
  );
}

function formatFileSubtitle(item: any): string {
  const parts: string[] = [];
  if (item.file_size) {
    const mb = item.file_size / (1024 * 1024);
    parts.push(mb >= 1 ? `${mb.toFixed(1)} MB` : `${(item.file_size / 1024).toFixed(1)} KB`);
  }
  if (item.created_at) {
    parts.push(new Date(item.created_at).toLocaleDateString());
  }
  return parts.join(' · ');
}
